import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalToolsApi } from '../../services/api';
import { toast } from '../../stores';
import {
  Wrench, Plus, Trash2, Edit3, Power, ExternalLink,
  ChevronDown, Check, X, Globe, Lock,
} from 'lucide-react';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE'];
const AUTH_TYPES = [
  { value: 'none', label: 'None', icon: Globe },
  { value: 'api_key', label: 'API Key', icon: Lock },
  { value: 'bearer', label: 'Bearer Token', icon: Lock },
  { value: 'basic', label: 'Basic Auth', icon: Lock },
  { value: 'custom_header', label: 'Custom Header', icon: Lock },
];

const METHOD_COLORS = {
  GET: 'text-nexus-accent-blue bg-nexus-accent-mint/10',
  POST: 'text-blue-400 bg-blue-500/10',
  PUT: 'text-amber-400 bg-amber-500/10',
  DELETE: 'text-red-400 bg-red-500/10',
};

const PortalTools = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editTool, setEditTool] = useState(null);

  const { data: tools = [], isLoading } = useQuery({
    queryKey: ['portal-tools'],
    queryFn: () => portalToolsApi.list().then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => portalToolsApi.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['portal-tools'] }); toast.success('Tool deleted'); },
    onError: () => toast.error('Failed to delete tool'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }) => portalToolsApi.update(id, { is_enabled: enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-tools'] }),
  });

  return (
    <div className="max-w-4xl mx-auto" data-testid="portal-tools-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-heading font-bold text-nexus-text-primary" data-testid="tools-page-title">Tools & Functions</h1>
          <p className="text-sm text-nexus-text-tertiary mt-1">Register external APIs as tools your AI assistant can call during conversations</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => { setEditTool(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-nexus-accent-mint text-nexus-bg-primary text-sm font-medium"
          data-testid="add-tool-btn"
        >
          <Plus size={16} />
          Add Tool
        </motion.button>
      </div>

      {/* Tool List */}
      {isLoading ? (
        <div className="text-center py-16 text-nexus-text-tertiary">Loading tools...</div>
      ) : tools.length === 0 ? (
        <EmptyTools onAdd={() => setShowForm(true)} />
      ) : (
        <div className="space-y-3">
          {tools.map((tool, i) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              index={i}
              onEdit={() => { setEditTool(tool); setShowForm(true); }}
              onDelete={() => deleteMutation.mutate(tool.id)}
              onToggle={(enabled) => toggleMutation.mutate({ id: tool.id, enabled })}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <ToolFormModal
            tool={editTool}
            onClose={() => { setShowForm(false); setEditTool(null); }}
            onSaved={() => { setShowForm(false); setEditTool(null); queryClient.invalidateQueries({ queryKey: ['portal-tools'] }); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ToolCard = ({ tool, index, onEdit, onDelete, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className={`border rounded-xl p-4 transition-colors ${
      tool.is_enabled
        ? 'bg-nexus-bg-secondary/60 border-nexus-border-subtle'
        : 'bg-nexus-bg-secondary/30 border-nexus-border-subtle opacity-60'
    }`}
    data-testid={`tool-card-${tool.name}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-nexus-accent-mint/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Wrench size={18} className="text-nexus-accent-mint" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-nexus-text-primary">{tool.name}</h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${METHOD_COLORS[tool.http_method] || 'text-nexus-text-secondary bg-white/5'}`}>
              {tool.http_method}
            </span>
            {!tool.is_enabled && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-nexus-text-tertiary">DISABLED</span>
            )}
          </div>
          <p className="text-xs text-nexus-text-tertiary mt-0.5 max-w-md">{tool.description}</p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-nexus-text-tertiary">
            <span className="flex items-center gap-1"><ExternalLink size={10} /> {tool.endpoint_url.length > 40 ? tool.endpoint_url.slice(0, 40) + '...' : tool.endpoint_url}</span>
            {tool.usage_count > 0 && <span>{tool.usage_count} calls</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onToggle(!tool.is_enabled)}
          className={`p-1.5 rounded-lg transition-colors ${tool.is_enabled ? 'text-nexus-accent-mint hover:bg-nexus-accent-mint/10' : 'text-nexus-text-tertiary hover:bg-nexus-bg-tertiary/50'}`}
          data-testid={`tool-toggle-${tool.name}`}>
          <Power size={14} />
        </button>
        <button onClick={onEdit}
          className="p-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors"
          data-testid={`tool-edit-${tool.name}`}>
          <Edit3 size={14} />
        </button>
        <button onClick={onDelete}
          className="p-1.5 rounded-lg text-nexus-text-tertiary hover:text-red-400 hover:bg-red-500/5 transition-colors"
          data-testid={`tool-delete-${tool.name}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  </motion.div>
);

const EmptyTools = ({ onAdd }) => (
  <div className="text-center py-16" data-testid="tools-empty-state">
    <div className="w-14 h-14 rounded-2xl bg-nexus-accent-mint/10 flex items-center justify-center mx-auto mb-4">
      <Wrench size={24} className="text-nexus-accent-mint" />
    </div>
    <h3 className="text-sm font-semibold text-nexus-text-primary mb-1">No tools registered</h3>
    <p className="text-xs text-nexus-text-tertiary max-w-sm mx-auto mb-4">
      Register external APIs as tools so your AI assistant can call them during conversations — like fetching live data, running calculations, or querying databases.
    </p>
    <button onClick={onAdd}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-nexus-accent-mint/10 text-nexus-accent-mint text-sm hover:bg-nexus-accent-mint/15 transition-colors"
      data-testid="add-first-tool-btn">
      <Plus size={14} /> Add your first tool
    </button>
  </div>
);

const ToolFormModal = ({ tool, onClose, onSaved }) => {
  const [name, setName] = useState(tool?.name || '');
  const [description, setDescription] = useState(tool?.description || '');
  const [httpMethod, setHttpMethod] = useState(tool?.http_method || 'POST');
  const [endpointUrl, setEndpointUrl] = useState(tool?.endpoint_url || '');
  const [authType, setAuthType] = useState(tool?.auth_type || 'none');
  const [authConfig, setAuthConfig] = useState(tool?.auth_config || {});
  const [params, setParams] = useState(() => {
    if (tool?.input_schema?.properties) {
      return Object.entries(tool.input_schema.properties).map(([k, v]) => ({
        key: k,
        type: v.type || 'string',
        description: v.description || '',
        required: (tool.input_schema.required || []).includes(k),
      }));
    }
    return [];
  });
  const [saving, setSaving] = useState(false);

  const addParam = () => setParams(prev => [...prev, { key: '', type: 'string', description: '', required: false }]);
  const removeParam = (i) => setParams(prev => prev.filter((_, idx) => idx !== i));
  const updateParam = (i, field, value) => setParams(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

  const handleSave = async () => {
    if (!name.trim() || !description.trim() || !endpointUrl.trim()) {
      toast.error('Name, description, and endpoint URL are required');
      return;
    }
    setSaving(true);
    const properties = {};
    const required = [];
    params.forEach(p => {
      if (p.key.trim()) {
        properties[p.key.trim()] = { type: p.type, description: p.description };
        if (p.required) required.push(p.key.trim());
      }
    });
    const payload = {
      name: name.trim(),
      description: description.trim(),
      http_method: httpMethod,
      endpoint_url: endpointUrl.trim(),
      auth_type: authType,
      auth_config: authConfig,
      input_schema: { type: 'object', properties, required },
    };
    try {
      if (tool?.id) {
        await portalToolsApi.update(tool.id, payload);
        toast.success('Tool updated');
      } else {
        await portalToolsApi.create(payload);
        toast.success('Tool created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save tool');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-nexus-bg-secondary border border-nexus-border-subtle rounded-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="tool-form-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-nexus-border-subtle">
          <h2 className="text-base font-heading font-semibold text-nexus-text-primary">
            {tool ? 'Edit Tool' : 'Add New Tool'}
          </h2>
          <button onClick={onClose} className="text-nexus-text-tertiary hover:text-nexus-text-primary">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Name */}
          <FormField label="Tool Name" testid="tool-name-input">
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. get_weather"
              className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none focus:border-nexus-accent-mint/30" />
          </FormField>

          {/* Description */}
          <FormField label="Description" testid="tool-desc-input">
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What does this tool do? The AI uses this to decide when to call it."
              rows={2}
              className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none focus:border-nexus-accent-mint/30 resize-none" />
          </FormField>

          {/* Method + URL */}
          <div className="grid grid-cols-[100px_1fr] gap-2">
            <FormField label="Method" testid="tool-method-select">
              <select value={httpMethod} onChange={e => setHttpMethod(e.target.value)}
                className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-2 py-2 text-sm text-nexus-text-primary focus:outline-none">
                {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </FormField>
            <FormField label="Endpoint URL" testid="tool-url-input">
              <input value={endpointUrl} onChange={e => setEndpointUrl(e.target.value)}
                placeholder="https://api.example.com/endpoint"
                className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none focus:border-nexus-accent-mint/30" />
            </FormField>
          </div>

          {/* Auth */}
          <FormField label="Authentication" testid="tool-auth-select">
            <select value={authType} onChange={e => { setAuthType(e.target.value); setAuthConfig({}); }}
              className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary focus:outline-none">
              {AUTH_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </FormField>

          {authType === 'api_key' && (
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Header Name">
                <input value={authConfig.header_name || ''} onChange={e => setAuthConfig(prev => ({ ...prev, header_name: e.target.value }))}
                  placeholder="X-API-Key"
                  className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none" />
              </FormField>
              <FormField label="API Key">
                <input value={authConfig.api_key || ''} onChange={e => setAuthConfig(prev => ({ ...prev, api_key: e.target.value }))}
                  type="password" placeholder="sk-..."
                  className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none" />
              </FormField>
            </div>
          )}
          {authType === 'bearer' && (
            <FormField label="Bearer Token">
              <input value={authConfig.token || ''} onChange={e => setAuthConfig(prev => ({ ...prev, token: e.target.value }))}
                type="password" placeholder="Token..."
                className="w-full bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-3 py-2 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none" />
            </FormField>
          )}

          {/* Parameters */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-nexus-text-secondary">Parameters</label>
              <button onClick={addParam} className="text-[11px] text-nexus-accent-mint hover:underline" data-testid="add-param-btn">+ Add Parameter</button>
            </div>
            <div className="space-y-2">
              {params.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={p.key} onChange={e => updateParam(i, 'key', e.target.value)} placeholder="name"
                    className="flex-1 bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-2 py-1.5 text-xs text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none" />
                  <select value={p.type} onChange={e => updateParam(i, 'type', e.target.value)}
                    className="w-20 bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-1 py-1.5 text-xs text-nexus-text-primary focus:outline-none">
                    <option value="string">string</option>
                    <option value="number">number</option>
                    <option value="boolean">boolean</option>
                  </select>
                  <input value={p.description} onChange={e => updateParam(i, 'description', e.target.value)} placeholder="description"
                    className="flex-1 bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-lg px-2 py-1.5 text-xs text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none" />
                  <label className="flex items-center gap-1 text-[10px] text-nexus-text-tertiary cursor-pointer select-none">
                    <input type="checkbox" checked={p.required} onChange={e => updateParam(i, 'required', e.target.checked)} className="rounded" />
                    Req
                  </label>
                  <button onClick={() => removeParam(i)} className="text-nexus-text-tertiary hover:text-red-400"><X size={12} /></button>
                </div>
              ))}
              {params.length === 0 && (
                <p className="text-[11px] text-nexus-text-tertiary text-center py-2">No parameters defined</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-nexus-border-subtle">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/50 transition-colors" data-testid="tool-cancel-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-nexus-accent-mint text-nexus-bg-primary disabled:opacity-50 transition-opacity"
            data-testid="tool-save-btn">
            {saving ? 'Saving...' : tool ? 'Update Tool' : 'Create Tool'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const FormField = ({ label, testid, children }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-medium text-nexus-text-secondary mb-1">{label}</label>
    {children}
  </div>
);

export default PortalTools;
